import { asNoteId, asReferencedResourceId, NOTE_CATEGORIES, type NoteCategory, type NoteId } from "@repo/domain";
import { z } from "zod";
import { fhirRefPolySchema } from "./common.schemas";
import { newEntityIdSchema } from "./entity-id.schemas";
import { patientRefSchema } from "./patient.schemas";
import { practitionerRefSchema } from "./practitioner.schemas";
import { codeableConceptSchema } from "./schedule.schemas";
import { visitIdSchema } from "./visit.schemas";

export const clinicalNoteIdSchema: z.ZodType<NoteId, z.ZodTypeDef, string> = z.string().min(1).transform(asNoteId);

// ClinicalImpression.finding.itemReference polymorphic target types per FHIR —
// narrow to what our workflows actually produce.
const findingItemRefSchema = fhirRefPolySchema("Condition", "Observation", "Media", "DocumentReference").transform(
  (ref) => ({ ...ref, id: asReferencedResourceId(ref.id) }),
);

// Read off the domain's list, so a category added there is accepted here.
export const noteCategorySchema = z.enum(Object.values(NOTE_CATEGORIES) as [NoteCategory, ...NoteCategory[]]);

const noteFindingSchema = z.object({
  itemCodeableConcept: codeableConceptSchema.optional(),
  itemReference: findingItemRefSchema.optional(),
  basis: z.string().optional(),
});

export const createNoteInputSchema = z.object({
  id: newEntityIdSchema,
  patient: patientRefSchema,
  visitId: visitIdSchema.optional(),
  date: z.string().optional(),
  description: z.string().optional(),
  category: noteCategorySchema.optional(),
  summary: z.string().optional(),
  note: z.string().optional(),
  finding: z.array(noteFindingSchema).optional().default([]),
});
export type CreateNoteInput = z.infer<typeof createNoteInputSchema>;

// The full intent: the author is the signed-in practitioner, filled in by the
// router from the session — never the request body's.
export const draftNoteIntentSchema = createNoteInputSchema.extend({ assessor: practitionerRefSchema });
export type DraftNoteIntent = z.infer<typeof draftNoteIntentSchema>;

export const updateNoteInputSchema = z.object({
  noteId: clinicalNoteIdSchema,
  description: z.string().optional(),
  // Absent leaves the category alone; null clears it.
  category: noteCategorySchema.nullable().optional(),
  summary: z.string().optional(),
  note: z.string().optional(),
  finding: z.array(noteFindingSchema).optional(),
});
export type UpdateNoteInput = z.infer<typeof updateNoteInputSchema>;

/** Intent-driven: sign, void or reinstate by ID only (target status is implicit in the intent) */
export const clinicalNoteIdInputSchema = z.object({
  noteId: clinicalNoteIdSchema,
});
export type NoteIdInput = z.infer<typeof clinicalNoteIdInputSchema>;
